import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/data";
import logo from "@/assets/vistara-logo.png";

const fieldBase =
  "w-full bg-transparent border-0 border-b border-charcoal/25 rounded-none px-0 py-3 font-sans text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-terracotta transition-colors";

const DateField = ({ label, value, onChange, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" data-testid={testid} className={`${fieldBase} flex items-center justify-between text-left`}>
        <span className={value ? "text-charcoal" : "text-charcoal/40"}>
          {value ? format(value, "dd MMM yyyy") : label}
        </span>
        <CalendarIcon size={17} strokeWidth={1.5} className="text-terracotta" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0 bg-cream border border-charcoal/15" align="start">
      <Calendar mode="single" selected={value} onSelect={onChange} disabled={{ before: new Date() }} initialFocus />
    </PopoverContent>
  </Popover>
);

export const Booking = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: "", message: "" });
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone.");
      return;
    }
    setLoading(true);
    try {
      const lines = [
        "New enquiry for Vistara Resort and Homestay",
        `Name: ${form.name}`,
        `Email: ${form.email || "-"}`,
        `Phone: ${form.phone}`,
        `Check in: ${checkIn ? format(checkIn, "dd MMM yyyy") : "-"}`,
        `Check out: ${checkOut ? format(checkOut, "dd MMM yyyy") : "-"}`,
        `Guests: ${form.guests || "-"}`,
        `Message: ${form.message || "-"}`,
      ];
      const text = encodeURIComponent(lines.join("\n"));
      window.open(`https://wa.me/${CONTACT.whatsapp}?text=${text}`, "_blank", "noreferrer");
      toast.success("Opening WhatsApp — we'll continue there.");
      setForm({ name: "", email: "", phone: "", guests: "", message: "" });
      setCheckIn(null);
      setCheckOut(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="bg-cream py-24 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-14 lg:gap-24">
          {/* Left: contact + map */}
          <div>
            <Reveal>
              <img src={logo} alt={CONTACT.name} className="w-36 md:w-44 mb-8" />
            </Reveal>
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta mb-6">(Plan your stay)</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-charcoal mb-8">
                Come up to the <span className="italic text-mist">hills.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-sans text-charcoal/70 leading-relaxed max-w-md mb-10">
                Send us your dates and we&rsquo;ll hold a cottage for you. Prefer to talk? We&rsquo;re a call or message away.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="space-y-4 mb-10">
                <a data-testid="contact-whatsapp" href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full bg-mist/10 text-mist flex items-center justify-center group-hover:bg-terracotta group-hover:text-cream transition-all duration-300">
                    <MessageCircle size={18} strokeWidth={1.5} />
                  </span>
                  <span className="font-sans text-charcoal/80 group-hover:text-terracotta transition-colors">WhatsApp us</span>
                </a>
                <a data-testid="contact-phone" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full bg-mist/10 text-mist flex items-center justify-center group-hover:bg-terracotta group-hover:text-cream transition-all duration-300">
                    <Phone size={18} strokeWidth={1.5} />
                  </span>
                  <span className="font-sans text-charcoal/80 group-hover:text-terracotta transition-colors">{CONTACT.phone}</span>
                </a>
                <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full bg-mist/10 text-mist flex items-center justify-center group-hover:bg-terracotta group-hover:text-cream transition-all duration-300">
                    <Mail size={18} strokeWidth={1.5} />
                  </span>
                  <span className="font-sans text-charcoal/80 group-hover:text-terracotta transition-colors">{CONTACT.email}</span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-full bg-mist/10 text-mist flex items-center justify-center">
                    <MapPin size={18} strokeWidth={1.5} />
                  </span>
                  <span className="font-sans text-charcoal/70 text-sm max-w-xs">{CONTACT.address}</span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="rounded-md overflow-hidden border border-charcoal/15">
                <iframe
                  data-testid="location-map"
                  title="Vistara Resort and Homestay location"
                  src={CONTACT.mapEmbed}
                  className="w-full h-[260px] map-earthy"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <form data-testid="booking-form" onSubmit={submit} className="lg:pt-16">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <input data-testid="booking-name" className={fieldBase} placeholder="Full name" value={form.name} onChange={set("name")} />
                <input data-testid="booking-phone" className={fieldBase} placeholder="Phone" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="mb-6">
                <input data-testid="booking-email" type="email" className={fieldBase} placeholder="Email (optional)" value={form.email} onChange={set("email")} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <DateField label="Check in" value={checkIn} onChange={setCheckIn} testid="booking-checkin" />
                <DateField label="Check out" value={checkOut} onChange={setCheckOut} testid="booking-checkout" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <Select value={form.guests} onValueChange={(v) => setForm((f) => ({ ...f, guests: v }))}>
                  <SelectTrigger data-testid="booking-guests" className={`${fieldBase} h-auto [&>span]:text-charcoal`}>
                    <SelectValue placeholder="Guests" />
                  </SelectTrigger>
                  <SelectContent>
                    {["1 guest", "2 guests", "3 guests", "4 guests", "5+ guests"].map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-10">
                <textarea data-testid="booking-message" rows={3} className={`${fieldBase} resize-none`} placeholder="Anything we should know?" value={form.message} onChange={set("message")} />
              </div>

              <button
                data-testid="booking-submit"
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-10 py-4 bg-terracotta text-cream rounded-sm text-sm uppercase tracking-[0.2em] hover:brightness-110 hover:scale-[1.02] transition-all duration-300 disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
